import FileSaver from 'file-saver';
import { format } from 'date-fns';
import { useCallback } from 'react';
import axios from 'axios';

import i18n from '@/settings/i18n';
import { userListApi } from '@/services/api/userList';

import * as Sentry from '@sentry/browser';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

type Props = {
  organizationId: number;
  gloplaId: string;
};

export const useDownloadCsv = ({ organizationId, gloplaId }: Props) => {
  const { apiErrorHandler } = useApiErrorHandler();
  const onDownloadCsvClick = useCallback<
    (e: React.MouseEvent<HTMLAnchorElement>) => Promise<void>
  >(
    async (e) => {
      e.preventDefault();

      try {
        await downloadCsv(organizationId, gloplaId);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response !== undefined) {
          if (err.response && err.response.status === 401) {
            try {
              localStorage.clear();
            } catch (e) {
              Sentry.captureException(e);
            }
          }
          apiErrorHandler(err);
        }
      }
    },
    [organizationId, gloplaId, apiErrorHandler],
  );

  return { onDownloadCsvClick };
};

const downloadCsv = async (organizationId: number, gloplaId: string) => {
  // BOMやBlobを理解してJavaScriptでCSVを出力する - Qiita
  // https://qiita.com/megadreams14/items/b4521308d5be65f0c544
  const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

  const datetime = format(new Date(), 'yyyyMMdd');
  const { data } = await userListApi.getUsersCSV(organizationId);
  const blob = new Blob([bom, data], {
    type: 'application/octet-stream',
  });

  FileSaver.saveAs(
    blob,
    i18n.t('text.user.csv.fileName', { gloplaId, datetime }),
  );
};
